import React from "react"
import { DateTime } from "luxon"
import {
  UNIFIEDDATE_RANGE,
  UNIFIEDDATE_VALUES,
} from "../consts/CompoundRangeDatePicker"
import {
  BURNCHART_ESTIMATION_DEFAULT_SPREAD,
  BURNCHART_ESTIMATION_TYPES,
  CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM,
  CLIENT_ESTIMATION_TYPES,
  CLIENT_REPORT_BUDGET_STATES,
  CLOSE_TYPE_LABELS,
  CLOSE_TYPES,
  SPRINT_DURATION_DAYS,
  TOTAL_CLIENT_ESTIMATION,
  weekToBlockDivider,
} from "../consts/modules/clientReport.consts"
import DetailedEstimationLabelTooltip from "../views/clientReport/components/DetailedEstimationLabelTooltip.compound"
import { getValidLuxonDate } from "./date.utils"
import { capitalizeFirstLetter } from "./strings.utils"
import { isObjectEmpty } from "./object.utils"
import { formatDecimals, numberFormatter } from "./numbers.utils"

export const validateIsPeriodTypeSprint = (period) => {
  return (
    period &&
    (period === UNIFIEDDATE_RANGE.SPRINT ||
      period === UNIFIEDDATE_VALUES.BLOCK ||
      period === UNIFIEDDATE_VALUES.CYCLE)
  )
}

/**
 * Converts sprint point progress data into a valid format for a recharts doughnut chart
 *
 * @param {Object} chartData
 * @param {number} chartData.totalCompletedPoints
 * @param {number} chartData.totalPointsToComplete
 * @returns {Array}
 */
export const formatPointProgressDoughnutChartData = (data) => {
  return [
    {
      data: [
        {
          name: "Avance actual",
          value: data?.totalCompletedPoints,
          color: "var(--primary-color)",
          extraInfo: {
            label: "Avance actual",
          },
        },
        {
          name: "Puntos faltantes",
          value: data?.totalPointsToComplete - data?.totalCompletedPoints,
          color: "light-dark(#DCE1E8, #535C69)",
          extraInfo: {
            label: null,
          },
        },
      ],
    },
  ]
}

/**
 * Calculates the estimated number of remaining sprints needed to complete current points based on average points per sprint
 *
 * @param {Object} chartData
 * @param {number} chartData.averagePointsPerSprint
 * @param {number} chartData.totalPointsToComplete
 * @param {number} chartData.totalCompletedPoints
 * @returns {number} Estimated number of remaining sprints
 */
export const getRemainingSprintsNumber = (data) => {
  if (
    data?.averagePointsPerSprint == null ||
    data?.totalPointsToComplete == null
  ) {
    return 0
  }

  const remainingPoints = data.totalPointsToComplete - data.totalCompletedPoints

  if (remainingPoints <= 0) return 0

  return Math.ceil(remainingPoints / data.averagePointsPerSprint)
}

/**
 * Calculates the estimated end date for completing current points based on average sprint duration and remaining sprints.
 *
 * @param {Object} chartData
 * @param {number} chartData.averageSprintDurationInDays
 * @param {number} chartData.averagePointsPerSprint
 * @returns {string|null} Estimated end date in "dd/MM/yyyy" format or null if data is insufficient
 */
export const getEstimatedEndDateToCompletePoints = (
  data,
  dateTimeOptions = {
    format: "dd/MM/yyyy",
  }
) => {
  const remainingSprints = getRemainingSprintsNumber(data)
  if (remainingSprints === 0) {
    return DateTime.now().toFormat(dateTimeOptions?.format || "dd/MM/yyyy")
  }
  const sprintDurationInDays =
    data?.averageSprintDurationInDays || SPRINT_DURATION_DAYS

  if (!remainingSprints || !data?.averagePointsPerSprint) return null
  return DateTime.now()
    .plus({ days: remainingSprints * sprintDurationInDays })
    .toFormat(dateTimeOptions?.format || "dd/MM/yyyy")
}

/**
 * Calculates the remaining weeks of budget based on total budget, total billed time, and average weekly consumption.
 *
 * @param {Object} params
 * @param {number|String} params.totalBudget
 * @param {number|String} params.totalBilledTime
 * @param {number|String} params.averageWeeklyConsumption
 * @returns {number} Remaining weeks of budget
 */
export const getBudgetRemainingWeeks = ({
  totalBudget,
  totalBilledTime,
  averageWeeklyConsumption,
}) => {
  if (
    !totalBudget ||
    !totalBilledTime ||
    !averageWeeklyConsumption ||
    averageWeeklyConsumption <= 0
  ) {
    return 0
  }

  const remainingBudget = totalBudget - totalBilledTime
  if (remainingBudget <= 0) {
    return 0
  }
  const remainingWeeks = remainingBudget / averageWeeklyConsumption
  return remainingWeeks
}

/**
 * Determines the budget state based on the number of remaining weeks
 *
 * @param {number} remainingWeeks
 * @returns {Object} Budget state object containing label, color, iconClassName, and tooltipValue
 */
export const getBudgetState = (remainingWeeks, hasBudget, remainingHours) => {
  if (!hasBudget) {
    return CLIENT_REPORT_BUDGET_STATES.WITHOUT_BUDGET
  }
  if (remainingHours === 0) {
    return CLIENT_REPORT_BUDGET_STATES.FINISHED
  } else if (
    remainingWeeks >= CLIENT_REPORT_BUDGET_STATES.OPTIMAL.minRemainingWeeks
  ) {
    return CLIENT_REPORT_BUDGET_STATES.OPTIMAL
  } else if (
    remainingWeeks >= CLIENT_REPORT_BUDGET_STATES.IN_RISK.minRemainingWeeks
  ) {
    return CLIENT_REPORT_BUDGET_STATES.IN_RISK
  } else {
    return CLIENT_REPORT_BUDGET_STATES.CRITICAL
  }
}

/**
 * Calculates the estimated budget end date based on the number of remaining weeks
 *
 * @param {number} remainingWeeks
 * @returns {DateTime}
 */
export const getEstimatedBudgetEndWithRemainingWeeks = (remainingWeeks) => {
  if (remainingWeeks <= 0) {
    return "-"
  }
  const today = DateTime.now()
  const estimatedEndDate = today.plus({ weeks: remainingWeeks })
  return estimatedEndDate.toFormat("dd/MM/yyyy")
}

/**
 * Calculates the budget progress percentage based on total budget and total billed time
 *
 * @param {Object} data
 * @param {number} data.totalBudget
 * @param {number} data.totalBilledTime
 * @returns {number|string} Progress percentage of the budget
 */
export const getBudgetProgress = (data) => {
  const totalBudget = Number(data?.totalBudget)
  const totalBilledTime = Number(data?.totalBilledTime)

  if (
    totalBudget == null ||
    totalBilledTime == null ||
    isNaN(totalBudget) ||
    isNaN(totalBilledTime)
  ) {
    return "-"
  }

  if (totalBudget <= 0) {
    return 0
  }

  const progress = (totalBilledTime / totalBudget) * 100

  if (!Number.isFinite(progress)) return 0

  return Math.min(Math.max(progress, 0), 100)
}

/**
 * Generates an array of empty client sprint detailed estimation data
 *
 * @returns {Array} An array of objects representing empty client sprint detailed estimation data
 */
export const getEmptyClientSprintDetailedEstimationData = () => {
  const emptyData = Object.values(CLIENT_ESTIMATION_TYPES).map((type) => ({
    ...type,
    value: null,
    remainingUserStories: null,
  }))
  return {
    totalPoints: {
      ...TOTAL_CLIENT_ESTIMATION,
      value: null,
      developed: null,
      remaining: null,
    },
    breakdown: emptyData,
  }
}

/**
 * Checks if the point type is a beta estimation type
 * Used to display [EST] badge
 *
 * @param {string} estimationType
 * @returns {Boolean}
 */
export const isEstimationTypeBeta = (estimationType) => {
  return (
    estimationType === CLIENT_ESTIMATION_TYPES.TO_ESTIMATE.estimationType ||
    estimationType === CLIENT_ESTIMATION_TYPES.TO_DESIGN.estimationType ||
    estimationType === CLIENT_ESTIMATION_TYPES.TO_DEFINE.estimationType
  )
}

const buildTooltipConfig = (description, remainingUserStories) => {
  return {
    position: "top",
    tooltipValue: (
      <DetailedEstimationLabelTooltip
        description={description}
        remainingUserStories={remainingUserStories}
      />
    ),
  }
}

/**
 * Formats sprint data for detailed estimation chart
 *
 * @param {import("../services/clientReport.service.types").ClientSprintDetailedEstimation | null} sprintData
 * @returns {Array} Formatted sprint data for detailed estimation chart
 */
export const formatSprintDetailedEstimationData = (sprintData) => {
  if (!sprintData || !sprintData.totalPoints) {
    return []
  }

  const totalPoints = {
    ...sprintData.totalPoints,
    value: sprintData.totalPoints.remaining,
    extraInfo: {
      label: sprintData.totalPoints.label,
      tooltipConfig: buildTooltipConfig(sprintData.totalPoints.description),
    },
  }

  const breakdownData = sprintData.breakdown.map((item) => ({
    ...item,
    extraInfo: {
      label: item.label,
      total: sprintData.totalPoints.remaining,
      tooltipConfig: buildTooltipConfig(
        item.description,
        item.remainingUserStories
      ),
    },
    name: item.label,
    value: isToDevelopEstimationBreakdown(item) ? item.remaining : item.value,
    color: item.color || "#E5E7EB",
  }))

  return [
    {
      data: [totalPoints, ...breakdownData],
    },
  ]
}

const isToDevelopEstimationBreakdown = (estimationBreakdown) => {
  return (
    estimationBreakdown?.estimationType ===
      CLIENT_ESTIMATION_TYPES.TO_DEVELOP.estimationType &&
    estimationBreakdown.developed !== undefined &&
    estimationBreakdown.remaining !== undefined
  )
}

export const getAverageConsumptionLabels = (clientConsumptionPeriodType) => {
  switch (clientConsumptionPeriodType) {
    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.MONTHLY:
      return {
        historic: "Histórico",
        secondLabel: "Últimos 6 meses",
        thirdLabel: "Últimos 3 meses",
        fourthLabel: "Mes anterior",
      }
    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.WEEKLY:
      return {
        historic: "Histórico",
        secondLabel: "Últimas 24 semanas",
        thirdLabel: "Últimas 12 semanas",
        fourthLabel: "Últimas 8 semanas",
      }
    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.SPRINT:
      return {
        historic: "Histórico",
        secondLabel: "Últimos 24 sprints",
        thirdLabel: "Últimos 12 sprints",
        fourthLabel: "Últimos 8 sprints",
      }
    default:
      return {}
  }
}

export const getAverageConsumptionDataByPeriodType = ({
  clientConsumptionPeriodType,
  data = {},
}) => {
  if (isObjectEmpty(data)) return {}
  switch (clientConsumptionPeriodType) {
    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.MONTHLY:
      return {
        historic: data.totalBilledTime / data.totalMonths,
        secondLabel: "Últimos 6 meses",
        second: data.lastSixMonthBilledTime / 6,
        thirdLabel: "Últimos 3 meses",
        third: data.lastThreeMonthBilledTime / 3,
        fourthLabel: "Mes anterior",
        fourth: data.lastMonthBilledTime,
      }

    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.WEEKLY:
      return {
        historic: data.totalBilledTime / data.totalMonths / 4,
        secondLabel: "Últimas 24 semanas",
        second: data.lastTwentyFourWeeksTruncatedMean,
        thirdLabel: "Últimas 12 semanas",
        third: data.lastTwelveWeeksTruncatedMean,
        fourthLabel: "Últimas 8 semanas",
        fourth: data.lastEightWeeksTruncatedMean,
      }

    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.SPRINT:
      return {
        historic: data.totalBilledTimeSinceFirstSprint / data.totalSprints,
        secondLabel: "Últimos 24 sprints",
        second: data.lastTwentyFourSprintsTruncatedMean,
        thirdLabel: "Últimos 12 sprints",
        third: data.lastTwelveSprintsTruncatedMean,
        fourthLabel: "Últimos 8 sprints",
        fourth: data.lastEightSprintsTruncatedMean,
      }

    default:
      return {}
  }
}

export const isPeriodInvalid = (period) => {
  return !period || isObjectEmpty(period) || !period.id || period?.id <= 0
}
export const getSelectedPeriodTitle = ({
  startDate,
  clientConsumptionPeriodType,
  period = { sprintNumber: 0 },
  closeType = null,
}) => {
  if (!startDate || !clientConsumptionPeriodType) {
    return
  }
  const periodStartDate = getValidLuxonDate(startDate)
  switch (clientConsumptionPeriodType) {
    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.WEEKLY:
      return `Semana ${periodStartDate?.toFormat("WW/yy")}`
    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.MONTHLY:
      return capitalizeFirstLetter(periodStartDate?.toFormat("LLLL yyyy"))
    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.SPRINT:
      return `Sprint ${closeType === CLOSE_TYPES.TIMEOUT ? CLOSE_TYPE_LABELS.TIMEOUT.toLowerCase() : period.sprintNumber}`
    default:
      return null
  }
}

export const getBilledTimeTooltipByPeriod = (clientConsumptionPeriodType) => {
  if (!clientConsumptionPeriodType) {
    return ""
  }
  switch (clientConsumptionPeriodType) {
    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.WEEKLY:
      return "Horas facturadas de la semana seleccionada"
    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.MONTHLY:
      return "Horas facturadas del mes seleccionado"
    case CLIENT_CONSUMPTION_PERIOD_TYPE_ENUM.SPRINT:
      return "Horas facturadas del sprint seleccionado"
    default:
      return null
  }
}

/**
 * Calculates the estimated finish sprint number for nominal, optimistic, or pessimistic modes.
 *
 * @param {Object} params
 * @param {number} params.totalCompletedPoints
 * @param {number} params.totalPointsToComplete
 * @param {number} params.averagePointsPerSprint
 * @param {string} params.mode See BURNCHART_ESTIMATION_TYPES
 * @param {number} params.spread Between 0 and 1, default is 0.2
 * @returns {number} Estimated finish sprint number
 */
export const calculateEstimatedFinishSprintNumber = ({
  totalCompletedPoints,
  totalPointsToComplete,
  averagePointsPerSprint,
  mode = BURNCHART_ESTIMATION_TYPES.NOMINAL,
  spread = BURNCHART_ESTIMATION_DEFAULT_SPREAD,
}) => {
  if (!averagePointsPerSprint) {
    return 0
  }
  if (Object.values(BURNCHART_ESTIMATION_TYPES).indexOf(mode) === -1) {
    throw new Error(
      `Invalid mode: ${mode}. Must be one of ${Object.values(BURNCHART_ESTIMATION_TYPES).join(", ")}`
    )
  }
  const modeSpreadFactor = {
    [BURNCHART_ESTIMATION_TYPES.NOMINAL]: 1,
    [BURNCHART_ESTIMATION_TYPES.OPTIMISTIC]: 1 - spread,
    [BURNCHART_ESTIMATION_TYPES.PESSIMISTIC]: 1 + spread,
  }
  const spreadFactor = modeSpreadFactor[mode]
  const remainingPoints = totalPointsToComplete - totalCompletedPoints
  const nominalEstimation = remainingPoints / averagePointsPerSprint
  // return Math.ceil(nominalEstimation * spreadFactor)
  return parseFloat((nominalEstimation * spreadFactor)?.toFixed(2))
}

/**
 * Generates future projected sprints based on the last sprint and estimated sprints.
 *
 * @param {Object} params
 * @param {number} params.startSprintNumber Last real sprint number
 * @param {number} params.lastSprintTotalCompletedPoints
 * @param {number} params.totalPointsToComplete
 * @param {number} params.estimatedSprint
 * @param {number} params.optimisticSprint
 * @param {number} params.pessimisticSprint
 * @param {number} params.extraSprintsOffset Optional, only to add extra space at the end of the burnchart
 * @returns {Array} Array of projected sprints
 */
export const generateFutureProjectedSprints = ({
  startSprintNumber,
  lastSprintTotalCompletedPoints,
  totalPointsToComplete,
  estimatedSprint,
  optimisticSprint,
  pessimisticSprint,
  extraSprintsOffset = 1,
}) => {
  let result = []
  const maxSprint = Math.max(
    estimatedSprint,
    optimisticSprint,
    pessimisticSprint
  )
  const totalSprintsToGenerate =
    maxSprint - startSprintNumber + extraSprintsOffset
  result.push({
    sprintNumber: startSprintNumber,
    totalPointsToComplete,
    projectedNominalTotalCompletedPoints: lastSprintTotalCompletedPoints,
    projectedOptimisticTotalCompletedPoints: lastSprintTotalCompletedPoints,
    projectedPessimisticTotalCompletedPoints: lastSprintTotalCompletedPoints,
  })
  for (let i = 1; i <= totalSprintsToGenerate; i++) {
    const sprintNumber = startSprintNumber + i
    if (estimatedSprint > sprintNumber && estimatedSprint < sprintNumber + 1) {
      result.push({
        sprintNumber: estimatedSprint,
        totalPointsToComplete,
        projectedNominalTotalCompletedPoints: totalPointsToComplete,
        projectedOptimisticTotalCompletedPoints: null,
        projectedPessimisticTotalCompletedPoints: null,
      })
      continue
    }
    if (
      optimisticSprint > sprintNumber &&
      optimisticSprint < sprintNumber + 1
    ) {
      result.push({
        sprintNumber: optimisticSprint,
        totalPointsToComplete,
        projectedNominalTotalCompletedPoints: null,
        projectedOptimisticTotalCompletedPoints: totalPointsToComplete,
        projectedPessimisticTotalCompletedPoints: null,
      })
      continue
    }
    if (
      pessimisticSprint > sprintNumber &&
      pessimisticSprint < sprintNumber + 1
    ) {
      result.push({
        sprintNumber: pessimisticSprint,
        totalPointsToComplete,
        projectedNominalTotalCompletedPoints: null,
        projectedOptimisticTotalCompletedPoints: null,
        projectedPessimisticTotalCompletedPoints: totalPointsToComplete,
      })
      continue
    }
    result.push({
      sprintNumber,
      totalPointsToComplete,
      projectedNominalTotalCompletedPoints:
        estimatedSprint === sprintNumber ? totalPointsToComplete : null,
      projectedOptimisticTotalCompletedPoints:
        optimisticSprint === sprintNumber ? totalPointsToComplete : null,
      projectedPessimisticTotalCompletedPoints:
        pessimisticSprint === sprintNumber ? totalPointsToComplete : null,
    })
  }
  result = fillNullsLinearly(result, "projectedNominalTotalCompletedPoints")
  result = fillNullsLinearly(result, "projectedOptimisticTotalCompletedPoints")
  result = fillNullsLinearly(result, "projectedPessimisticTotalCompletedPoints")
  return result
}

/**
 * Wraps calculateEstimatedFinishSprintNumber and generateFutureProjectedSprints in a single call
 *
 * @param {Object} params
 * @param {Array} params.realSprints
 * @param {Object} params.lastSprint
 * @param {number} params.spread
 * @returns {Object} Object containing projected sprints and estimated sprints
 */
export const calculateBurnchartProjection = ({
  realSprints = [],
  lastSprint,
  spread = BURNCHART_ESTIMATION_DEFAULT_SPREAD,
}) => {
  const averagePointsPerSprint = lastSprint?.averagePointsPerSprint
  const totalPointsToComplete = lastSprint?.totalPointsToComplete
  const totalCompletedPoints = lastSprint?.totalCompletedPoints

  const estimatedSprint =
    lastSprint.sprintNumber +
    calculateEstimatedFinishSprintNumber({
      totalCompletedPoints,
      totalPointsToComplete,
      averagePointsPerSprint,
      spread,
    })
  const optimisticSprint =
    lastSprint.sprintNumber +
    calculateEstimatedFinishSprintNumber({
      totalCompletedPoints,
      totalPointsToComplete,
      averagePointsPerSprint,
      mode: BURNCHART_ESTIMATION_TYPES.OPTIMISTIC,
      spread,
    })
  const pessimisticSprint =
    lastSprint.sprintNumber +
    calculateEstimatedFinishSprintNumber({
      totalCompletedPoints,
      totalPointsToComplete,
      averagePointsPerSprint,
      mode: BURNCHART_ESTIMATION_TYPES.PESSIMISTIC,
      spread,
    })
  realSprints[realSprints.length - 1] = {
    ...realSprints[realSprints.length - 1],
    projectedNominalTotalCompletedPoints: totalCompletedPoints,
    projectedOptimisticTotalCompletedPoints: totalCompletedPoints,
    projectedPessimisticTotalCompletedPoints: totalCompletedPoints,
  }
  const projectedSprints = generateFutureProjectedSprints({
    startSprintNumber: lastSprint.sprintNumber,
    lastSprintTotalCompletedPoints: totalCompletedPoints,
    totalPointsToComplete,
    estimatedSprint,
    optimisticSprint,
    pessimisticSprint,
    averagePointsPerSprint,
  })
  return {
    sprints: [
      ...realSprints.slice(0, -1),
      {
        ...realSprints[realSprints.length - 1],
        ...projectedSprints[0],
      },
      ...projectedSprints.slice(1),
    ],
    estimatedSprint,
    optimisticSprint,
    pessimisticSprint,
  }
}

// export const fillNullsLinearly = (data, key, xKey = "sprintNumber") => {
//   const knownIndexes = data
//     .map((point, idx) =>
//       point[key] != null ? { idx, value: point[key] } : null
//     )
//     .filter(Boolean)
//   if (knownIndexes.length < 2) return data
//   for (let i = 0; i < knownIndexes.length - 1; i++) {
//     const { idx: startIdx, value: startValue } = knownIndexes[i]
//     const { idx: endIdx, value: endValue } = knownIndexes[i + 1]
//     const step = (endValue - startValue) / (endIdx - startIdx)
//     for (let j = startIdx + 1; j < endIdx; j++) {
//       if (data[j][key] == null) {
//         data[j][key] = startValue + step * (j - startIdx)
//       }
//     }
//   }

//   return data
// }

/**
 * Fills missing values in the data array by linearly interpolating between known values.
 *
 * @param {Array} data
 * @param {String} key
 * @returns {Array} The data array with missing values filled
 */
export const fillNullsLinearly = (data, key, xKey = "sprintNumber") => {
  const known = data
    .filter((point) => point[key] != null && point[xKey] != null)
    .map((point) => ({ x: point[xKey], y: point[key] }))
  if (known.length < 2) return data
  for (let i = 0; i < known.length - 1; i++) {
    const { x: x0, y: y0 } = known[i]
    const { x: x1, y: y1 } = known[i + 1]
    for (const point of data) {
      const x = point[xKey]
      if (x == null || point[key] != null || x <= x0 || x >= x1) continue
      point[key] = y0 + ((x - x0) * (y1 - y0)) / (x1 - x0)
    }
  }
  return data
}

export const formatClientsHistoryValues = (value, decimals = 2) => {
  if (value === Infinity || value === -Infinity) {
    return numberFormatter(formatDecimals(0, decimals))
  }
  if (isNaN(value) || value === null || value === undefined) return "0"
  return numberFormatter(formatDecimals(value, decimals))
}

export const getDevelopmentCompletionEstimate = (data) => {
  if (!data) return null

  const avgPointsPerSprint = Number(data.averagePointsPerSprint)
  const sprintDays = Number(data.averageSprintDurationInDays)
  const remainingPoints = Number(data.remainingPoints)
  const lastDate = data.lastClosedSprintEndDate

  if (
    isNaN(avgPointsPerSprint) ||
    isNaN(sprintDays) ||
    isNaN(remainingPoints) ||
    !lastDate
  ) {
    return null
  }

  if (remainingPoints === 0) {
    return {
      sprints: 0,
      weeks: 0,
      rawWeeks: 0,
      endDate: DateTime.fromISO(lastDate).toFormat("dd LLL yyyy"),
    }
  }

  const estimatedSprints = remainingPoints / avgPointsPerSprint
  const totalDays = estimatedSprints * sprintDays

  const baseDate = DateTime.fromISO(lastDate)
    .setZone("America/Mexico_City")
    .startOf("day")

  const start = baseDate.startOf("day")

  const normalizedStart =
    start.weekday === 6 ? start.plus({ days: 2 })
    : start.weekday === 7 ? start.plus({ days: 1 })
    : start

  const estimatedEndDate = addBusinessDays(
    normalizedStart,
    Math.round(totalDays)
  )

  const totalCalendarDays = estimatedEndDate
    .startOf("day")
    .diff(baseDate.startOf("day"), "days").days

  const calendarWeeks = totalCalendarDays / SPRINT_DURATION_DAYS

  return {
    sprints: Math.ceil(estimatedSprints),
    weeks: Math.ceil(calendarWeeks),
    rawWeeks: calendarWeeks,
    endDate: estimatedEndDate.toFormat("dd LLL yyyy"),
  }
}

const addBusinessDays = (startDate, daysToAdd) => {
  let date = startDate
  let added = 0

  while (added < daysToAdd) {
    date = date.plus({ days: 1 })

    const isWeekend = date.weekday === 6 || date.weekday === 7 // sáb/dom

    if (!isWeekend) {
      added++
    }
  }

  return date
}

/**
 * Converts estimated weeks to an estimated end date, excluding weekends and specific days,
 * approximating to the next business day if needed.
 *
 * @param {string|DateTime} referenceDate - Starting date (ISO string or Luxon DateTime)
 * @param {number} estimatedWeeks - Number of weeks to add
 * @param {Array<string>} excludeDays - Array of dates to exclude in ISO format (e.g., ["2026-06-15", "2026-06-16"])
 * @returns {DateTime} The estimated end date, adjusted to the next business day if needed
 */
export const convertWeekEstimnatedtoDateEstimated = (
  referenceDate,
  estimatedWeeks,
  excludeDays = []
) => {
  // 1. Convert reference date to Luxon DateTime
  const startDate =
    typeof referenceDate === "string" ?
      DateTime.fromISO(referenceDate)
        .setZone("America/Mexico_City")
        .startOf("day")
    : referenceDate.setZone("America/Mexico_City").startOf("day")

  // Build Set of excluded dates (parsed in UTC to preserve the date)
  const excludedDatesSet = new Set()
  if (Array.isArray(excludeDays) && excludeDays.length > 0) {
    excludeDays.forEach((date) => {
      let parsedDate
      if (typeof date === "string") {
        parsedDate = DateTime.fromISO(date, { zone: "utc" }).startOf("day")
      } else if (date instanceof DateTime) {
        parsedDate = date.toUTC().startOf("day")
      } else {
        return // Skip invalid formats
      }
      if (parsedDate.isValid) {
        excludedDatesSet.add(parsedDate.toISODate())
      }
    })
  }

  // Helper function to check if a date is a business day
  const isBusinessDay = (date) => {
    const isWeekend = date.weekday === 6 || date.weekday === 7 // Saturday or Sunday
    const isExcluded = excludedDatesSet.has(date.toISODate())
    return !isWeekend && !isExcluded
  }

  // 2. Add the estimated weeks to the reference date
  let resultDate = startDate.plus({ weeks: estimatedWeeks })

  // 3. If the result falls on a weekend or excluded day, move to the next business day
  while (!isBusinessDay(resultDate)) {
    resultDate = resultDate.plus({ days: 1 })
  }

  return resultDate
}

/**
 * Versión genérica de calculateEstimatedFinishPeriodNumber
 * Funciona tanto para sprints como para semanas
 */
export const calculateEstimatedFinishPeriodNumber = ({
  totalCompletedPoints,
  totalPointsToComplete,
  averagePointsPerPeriod,
  mode = BURNCHART_ESTIMATION_TYPES.NOMINAL,
  spread = BURNCHART_ESTIMATION_DEFAULT_SPREAD,
}) => {
  if (!averagePointsPerPeriod) {
    return 0
  }
  if (Object.values(BURNCHART_ESTIMATION_TYPES).indexOf(mode) === -1) {
    throw new Error(
      `Invalid mode: ${mode}. Must be one of ${Object.values(BURNCHART_ESTIMATION_TYPES).join(", ")}`
    )
  }
  const modeSpreadFactor = {
    [BURNCHART_ESTIMATION_TYPES.NOMINAL]: 1,
    [BURNCHART_ESTIMATION_TYPES.OPTIMISTIC]: 1 - spread,
    [BURNCHART_ESTIMATION_TYPES.PESSIMISTIC]: 1 + spread,
  }
  const spreadFactor = modeSpreadFactor[mode]
  const remainingPoints = totalPointsToComplete - totalCompletedPoints
  const nominalEstimation = remainingPoints / averagePointsPerPeriod
  return parseFloat((nominalEstimation * spreadFactor)?.toFixed(2))
}

/**
 * Versión genérica de generateFutureProjectedPeriods
 * Funciona tanto para sprints como para semanas
 */
export const generateFutureProjectedPeriods = ({
  startPeriodNumber,
  lastPeriodTotalCompletedPoints,
  totalPointsToComplete,
  estimatedPeriod,
  optimisticPeriod,
  pessimisticPeriod,
  periodKey = "sprintNumber",
  milestonePeriods = {},
  extraPeriodsOffset = 1,
}) => {
  let result = []
  const maxPeriod = Math.max(
    estimatedPeriod,
    optimisticPeriod,
    pessimisticPeriod,
    milestonePeriods?.nominal || 0,
    milestonePeriods?.optimistic || 0,
    milestonePeriods?.pessimistic || 0
  )
  const totalPeriodsToGenerate =
    maxPeriod - startPeriodNumber + extraPeriodsOffset

  result.push({
    [periodKey]: startPeriodNumber,
    totalPointsToComplete,
    projectedNominalTotalCompletedPoints: lastPeriodTotalCompletedPoints,
    projectedOptimisticTotalCompletedPoints: lastPeriodTotalCompletedPoints,
    projectedPessimisticTotalCompletedPoints: lastPeriodTotalCompletedPoints,
  })

  for (let i = 1; i <= totalPeriodsToGenerate; i++) {
    const periodNumber = startPeriodNumber + i

    const hasMilestoneNominal =
      milestonePeriods?.nominal >= periodNumber &&
      milestonePeriods?.nominal < periodNumber + 1
    const hasMilestoneOptimistic =
      milestonePeriods?.optimistic >= periodNumber &&
      milestonePeriods?.optimistic < periodNumber + 1
    const hasMilestonePessimistic =
      milestonePeriods?.pessimistic >= periodNumber &&
      milestonePeriods?.pessimistic < periodNumber + 1

    // Detectar puntos finales en este rango
    const hasEndNominal =
      (estimatedPeriod > periodNumber && estimatedPeriod < periodNumber + 1) ||
      Math.floor(estimatedPeriod) === periodNumber

    const hasEndOptimistic =
      (optimisticPeriod > periodNumber &&
        optimisticPeriod < periodNumber + 1) ||
      Math.floor(optimisticPeriod) === periodNumber

    const hasEndPessimistic =
      (pessimisticPeriod > periodNumber &&
        pessimisticPeriod < periodNumber + 1) ||
      Math.floor(pessimisticPeriod) === periodNumber

    if (
      hasMilestoneNominal ||
      hasMilestoneOptimistic ||
      hasMilestonePessimistic ||
      hasEndNominal ||
      hasEndOptimistic ||
      hasEndPessimistic
    ) {
      if (hasMilestoneNominal) {
        result.push({
          [periodKey]: milestonePeriods.nominal,
          totalPointsToComplete,
          projectedNominalTotalCompletedPoints:
            milestonePeriods.milestoneTotalPoints,
          projectedOptimisticTotalCompletedPoints: null,
          projectedPessimisticTotalCompletedPoints: null,
          isMilestone: true,
          milestoneType: "nominal",
        })
      }

      if (hasMilestoneOptimistic) {
        result.push({
          [periodKey]: milestonePeriods.optimistic,
          totalPointsToComplete,
          projectedNominalTotalCompletedPoints: null,
          projectedOptimisticTotalCompletedPoints:
            milestonePeriods.milestoneTotalPoints,
          projectedPessimisticTotalCompletedPoints: null,
          isMilestone: true,
          milestoneType: "optimistic",
        })
      }

      if (hasMilestonePessimistic) {
        result.push({
          [periodKey]: milestonePeriods.pessimistic,
          totalPointsToComplete,
          projectedNominalTotalCompletedPoints: null,
          projectedOptimisticTotalCompletedPoints: null,
          projectedPessimisticTotalCompletedPoints:
            milestonePeriods.milestoneTotalPoints,
          isMilestone: true,
          milestoneType: "pessimistic",
        })
      }

      if (hasEndNominal) {
        result.push({
          [periodKey]: estimatedPeriod + 0.001,
          totalPointsToComplete,
          projectedNominalTotalCompletedPoints: totalPointsToComplete,
          projectedOptimisticTotalCompletedPoints: null,
          projectedPessimisticTotalCompletedPoints: null,
          isEndPoint: true,
          endPointType: "nominal",
        })
      }

      if (hasEndOptimistic) {
        result.push({
          [periodKey]: optimisticPeriod + 0.001,
          totalPointsToComplete,
          projectedNominalTotalCompletedPoints: null,
          projectedOptimisticTotalCompletedPoints: totalPointsToComplete,
          projectedPessimisticTotalCompletedPoints: null,
          isEndPoint: true,
          endPointType: "optimistic",
        })
      }

      if (hasEndPessimistic) {
        result.push({
          [periodKey]: pessimisticPeriod + 0.001,
          totalPointsToComplete,
          projectedNominalTotalCompletedPoints: null,
          projectedOptimisticTotalCompletedPoints: null,
          projectedPessimisticTotalCompletedPoints: totalPointsToComplete,
          isEndPoint: true,
          endPointType: "pessimistic",
        })
      }
      continue
    }

    // Punto regular
    result.push({
      [periodKey]: periodNumber,
      totalPointsToComplete,
      projectedNominalTotalCompletedPoints:
        estimatedPeriod === periodNumber ? totalPointsToComplete : null,
      projectedOptimisticTotalCompletedPoints:
        optimisticPeriod === periodNumber ? totalPointsToComplete : null,
      projectedPessimisticTotalCompletedPoints:
        pessimisticPeriod === periodNumber ? totalPointsToComplete : null,
    })
  }

  result = fillNullsLinearly(
    result,
    "projectedNominalTotalCompletedPoints",
    periodKey
  )
  result = fillNullsLinearly(
    result,
    "projectedOptimisticTotalCompletedPoints",
    periodKey
  )
  result = fillNullsLinearly(
    result,
    "projectedPessimisticTotalCompletedPoints",
    periodKey
  )

  return result
}
/**
 * Versión genérica de calculateBurnchartProjection
 * Funciona tanto para sprints como para semanas
 */
export const calculateBurnchartProjectionGeneric = ({
  realPeriods = [],
  lastPeriod,
  periodKey = "sprintNumber",
  averageKey = "averagePointsPerSprint",
  spread = BURNCHART_ESTIMATION_DEFAULT_SPREAD,
  milestonePeriods = {},
}) => {
  const averagePointsPerPeriod = parseFloat(lastPeriod?.[averageKey])
  const totalPointsToComplete = lastPeriod?.totalPointsToComplete
  const totalCompletedPoints = lastPeriod?.totalCompletedPoints

  const estimatedPeriod =
    lastPeriod[periodKey] +
    calculateEstimatedFinishPeriodNumber({
      totalCompletedPoints,
      totalPointsToComplete,
      averagePointsPerPeriod,
      spread,
    })

  const optimisticPeriod =
    lastPeriod[periodKey] +
    calculateEstimatedFinishPeriodNumber({
      totalCompletedPoints,
      totalPointsToComplete,
      averagePointsPerPeriod,
      mode: BURNCHART_ESTIMATION_TYPES.OPTIMISTIC,
      spread,
    })

  const pessimisticPeriod =
    lastPeriod[periodKey] +
    calculateEstimatedFinishPeriodNumber({
      totalCompletedPoints,
      totalPointsToComplete,
      averagePointsPerPeriod,
      mode: BURNCHART_ESTIMATION_TYPES.PESSIMISTIC,
      spread,
    })

  realPeriods[realPeriods.length - 1] = {
    ...realPeriods[realPeriods.length - 1],
    projectedNominalTotalCompletedPoints: totalCompletedPoints,
    projectedOptimisticTotalCompletedPoints: totalCompletedPoints,
    projectedPessimisticTotalCompletedPoints: totalCompletedPoints,
  }

  const projectedPeriods = generateFutureProjectedPeriods({
    startPeriodNumber: lastPeriod[periodKey],
    lastPeriodTotalCompletedPoints: totalCompletedPoints,
    totalPointsToComplete,
    estimatedPeriod,
    optimisticPeriod,
    pessimisticPeriod,
    periodKey,
    milestonePeriods,
  })
  return {
    sprints: [
      ...realPeriods.slice(0, -1),
      {
        ...realPeriods[realPeriods.length - 1],
        ...projectedPeriods[0],
      },
      ...projectedPeriods.slice(1),
    ],
    estimatedSprint: estimatedPeriod,
    optimisticSprint: optimisticPeriod,
    pessimisticSprint: pessimisticPeriod,
  }
}

/**
 * Versión para modo presentación de calculateBurnchartProjection
 * Usa valores de semanas ya calculados en lugar de calcularlos internamente
 */
export const calculateBurnchartProjectionFromPrecalculated = ({
  realPeriods = [],
  lastPeriod,
  periodKey = "weekNumber",
  semanasNominales,
  semanasOptimistas,
  semanasPesimistas,
  milestonePeriods = {},
}) => {
  const totalPointsToComplete = lastPeriod?.totalPointsToComplete
  const totalCompletedPoints = lastPeriod?.totalCompletedPoints

  // Las semanas ya vienen calculadas, solo sumamos al periodo actual
  const estimatedPeriod = lastPeriod[periodKey] + semanasNominales
  const optimisticPeriod = lastPeriod[periodKey] + semanasOptimistas
  const pessimisticPeriod = lastPeriod[periodKey] + semanasPesimistas

  realPeriods[realPeriods.length - 1] = {
    ...realPeriods[realPeriods.length - 1],
    projectedNominalTotalCompletedPoints: totalCompletedPoints,
    projectedOptimisticTotalCompletedPoints: totalCompletedPoints,
    projectedPessimisticTotalCompletedPoints: totalCompletedPoints,
  }

  const projectedPeriods = generateFutureProjectedPeriods({
    startPeriodNumber: lastPeriod[periodKey],
    lastPeriodTotalCompletedPoints: totalCompletedPoints,
    totalPointsToComplete,
    estimatedPeriod,
    optimisticPeriod,
    pessimisticPeriod,
    periodKey,
    milestonePeriods,
  })

  return {
    sprints: [
      ...realPeriods.slice(0, -1),
      {
        ...realPeriods[realPeriods.length - 1],
        ...projectedPeriods[0],
      },
      ...projectedPeriods.slice(1),
    ],
    estimatedSprint: estimatedPeriod,
    optimisticSprint: optimisticPeriod,
    pessimisticSprint: pessimisticPeriod,
  }
}

/**
 * Obtiene el número de periodo (weekNumber/sprintNumber) a partir de una fecha.
 * 1) Si cae dentro de un periodo real con startDate/endDate, usa ese periodo.
 * 2) Si cae fuera del rango real, extrapola desde el último periodo real con fecha.
 */
export const getPeriodNumberFromDate = ({
  targetDate,
  periods = [],
  periodKey = "weekNumber",
}) => {
  if (!targetDate || !Array.isArray(periods) || periods.length === 0) {
    return null
  }

  const parseUtcDay = (value) => {
    if (!value) return null
    const dt = DateTime.fromISO(value, { zone: "utc" })
    return dt.isValid ? dt.startOf("day") : null
  }

  const target = parseUtcDay(targetDate)
  if (!target) return null

  const sorted = [...periods]
    .filter((p) => p?.[periodKey] != null)
    .sort((a, b) => a[periodKey] - b[periodKey])

  // 1) Match por rango real (startDate/endDate)
  for (const p of sorted) {
    const start = parseUtcDay(p?.startDate)
    const end = parseUtcDay(p?.endDate)
    if (!start || !end) continue

    if (target >= start && target <= end) {
      return p[periodKey]
    }
  }

  // 2) Extrapolación desde periodos reales con fecha
  const dated = sorted.filter(
    (p) => parseUtcDay(p?.startDate) && parseUtcDay(p?.endDate)
  )
  if (dated.length === 0) return null

  const first = dated[0]
  const last = dated[dated.length - 1]

  const firstStart = parseUtcDay(first.startDate)
  const lastEnd = parseUtcDay(last.endDate)

  // Antes del primer periodo real
  if (target < firstStart) {
    const weeksBefore = firstStart.diff(target, "weeks").weeks
    return parseFloat((first[periodKey] - weeksBefore).toFixed(2))
  }

  // Después del último periodo real (zona proyectada)
  if (target > lastEnd) {
    const weeksAfter = target.diff(lastEnd, "weeks").weeks
    return parseFloat((last[periodKey] + weeksAfter).toFixed(2))
  }

  return null
}

export const getProyectionInBlocksByWeeks = ({ weeks = 0 }) => {
  if (!weeks || isNaN(weeks) || weeks <= 0) return 0
  return Math.ceil(weeks / weekToBlockDivider)
}
